/**
 * Selects files, partitions by size, and reads accepted entries into the
 * base64 payload shape that genesis mutations expect. Used by the Match
 * Brief drop zone and the Share create/edit file pickers — both ride the
 * same GraphQL proxy and share its body cap.
 *
 * Returns an aggregate result instead of throwing on oversized files so the
 * caller can render localized rejection messages alongside successful reads.
 * A FileReader failure (rare; corrupted browser state) still bubbles via the
 * returned promise — callers route those through their UI error path.
 */

export interface IngestedFile {
  /** File name as the browser reported it. */
  name: string;
  /** Falls back to `application/octet-stream` when the browser gives no MIME. */
  mimeType: string;
  /** Raw byte size of the original File (not base64 length). */
  size: number;
  /** Base64 content with the `data:` prefix stripped. */
  content: string;
}

export type RejectionReason = "tooLarge" | "unsupportedType";

export interface RejectedFile {
  file: File;
  reason: RejectionReason;
}

export interface IngestResult {
  accepted: IngestedFile[];
  rejected: RejectedFile[];
}

export interface IngestOptions {
  /** Maximum bytes per file. Files larger than this are rejected, not read. */
  maxBytes: number;
  /** Optional browser-style accept list, e.g. "image/*,.pdf". */
  accept?: string;
}

export async function ingestFiles(
  files: FileList | File[] | null | undefined,
  { maxBytes, accept }: IngestOptions,
): Promise<IngestResult> {
  if (!files) return { accepted: [], rejected: [] };
  const all = Array.from(files);
  if (all.length === 0) return { accepted: [], rejected: [] };

  const okFiles: File[] = [];
  const rejected: RejectedFile[] = [];
  for (const file of all) {
    if (file.size > maxBytes) rejected.push({ file, reason: "tooLarge" });
    else if (!acceptsFile(file, accept)) rejected.push({ file, reason: "unsupportedType" });
    else okFiles.push(file);
  }

  const accepted = await Promise.all(okFiles.map(readAsIngestedFile));
  return { accepted, rejected };
}

/**
 * Whether `file` matches a browser-style accept list ("image/*,.pdf"). Split
 * out from `ingestFiles` so callers that read the file themselves (e.g.
 * BulkYamlImport, which needs raw text rather than the base64 payload shape
 * here) can still share the one accept-matching rule instead of re-deriving
 * it from the file extension.
 */
export function acceptsFile(file: File, accept?: string): boolean {
  const rules = (accept ?? "")
    .split(",")
    .map((rule) => rule.trim().toLowerCase())
    .filter(Boolean);
  if (rules.length === 0 || rules.includes("*/*")) return true;

  const type = file.type.toLowerCase();
  const name = file.name.toLowerCase();
  return rules.some((rule) => {
    if (rule.startsWith(".")) return name.endsWith(rule);
    if (rule.endsWith("/*")) return type.startsWith(rule.slice(0, -1));
    return type === rule;
  });
}

function readAsIngestedFile(file: File): Promise<IngestedFile> {
  return new Promise((resolve, reject) => {
    const reader = new FileReader();
    reader.onload = () => {
      const dataUrl = String(reader.result);
      const comma = dataUrl.indexOf(",");
      resolve({
        name: file.name,
        mimeType: file.type || "application/octet-stream",
        size: file.size,
        content: comma >= 0 ? dataUrl.slice(comma + 1) : dataUrl,
      });
    };
    reader.onerror = () => reject(reader.error);
    reader.readAsDataURL(file);
  });
}
